// src/lib/auth/actions.js
import { createClient } from '@/lib/supabase/client'

const supabase = createClient()

const WEB_URL = process.env.NEXT_PUBLIC_SITE_URL

export const authActions = {
  async signInWithOtp(email, redirectTo = null, options = {}) {
    try {
      // Extract display name from email (everything before @)
      const displayName = email.split('@')[0]

      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectTo || `${WEB_URL}/auth/callback`,
          shouldCreateUser: true,
          data: {
            display_name: displayName,
          },
          captchaToken: options.captchaToken || null,
        },
      })

      if (error) {
        console.error('OTP error details:', error)
        return { data: null, error }
      }

      return { data, error: null }
    } catch (err) {
      console.error('Unexpected auth error:', err)
      return {
        data: null,
        error: {
          message: 'An unexpected error occurred. Please try again.',
          details: err,
        },
      }
    }
  },

  async verifyOtp(email, token) {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      })

      if (error) {
        console.error('OTP verification error:', error)
        return { data: null, error }
      }

      return { data, error: null }
    } catch (err) {
      console.error('Unexpected OTP verification error:', err)
      return {
        data: null,
        error: {
          message: 'An unexpected error occurred. Please try again.',
          details: err,
        },
      }
    }
  },

  async resendOtp(email) {
    try {
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email,
      })

      if (error) {
        console.error('Resend OTP error:', error)
        return { data: null, error }
      }

      return { data, error: null }
    } catch (err) {
      console.error('Unexpected resend error:', err)
      return {
        data: null,
        error: {
          message: 'Failed to resend code. Please try again.',
          details: err,
        },
      }
    }
  },

  async updateUserTable(updates) {
    try {
      // Get current user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser()

      if (userError || !user) {
        return { data: null, error: userError || new Error('User not found') }
      }

      // Convert camelCase to snake_case for database columns
      const snakeCaseUpdates = Object.keys(updates).reduce((acc, key) => {
        const snakeKey = key.replace(
          /[A-Z]/g,
          (letter) => `_${letter.toLowerCase()}`
        )
        acc[snakeKey] = updates[key]
        return acc
      }, {})

      // Add updated_at timestamp
      snakeCaseUpdates.updated_at = new Date().toISOString()

      // Update users table (only updates the fields specified)
      const { data, error } = await supabase
        .from('users')
        .update(snakeCaseUpdates)
        .eq('id', user.id)
        .select()
        .single()

      if (error) {
        console.error('User table update error:', error)
        return { data: null, error }
      }

      return { data, error: null }
    } catch (err) {
      console.error('Unexpected user table update error:', err)
      return {
        data: null,
        error: {
          message: 'Failed to update user information.',
          details: err,
        },
      }
    }
  },

  async updateEmail(newEmail) {
    try {
      if (!newEmail) {
        return { error: { message: 'Email is required' } }
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(newEmail)) {
        return { error: { message: 'Invalid email format' } }
      }

      const { error } = await supabase.auth.updateUser({
        email: newEmail,
      })

      if (error) {
        console.error('Email update error:', error)
        return { error }
      }

      return { error: null }
    } catch (err) {
      console.error('Unexpected email update error:', err)
      return {
        error: {
          message: 'Failed to update email.',
          details: err,
        },
      }
    }
  },

  async deleteAccount() {
    try {
      const response = await fetch('/api/user/delete-account', {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to delete account')
      }

      const data = await response.json()
      return { error: null, data }
    } catch (error) {
      console.error('Delete account error:', error)
      return { error }
    }
  },

  async signOut() {
    const { error } = await supabase.auth.signOut()
    return { error }
  },
}
